'use client'

import { useEffect, useState } from 'react'
import { matchBandBarClass, matchBandTextClass } from '@/lib/match-bands'
import { cn } from '@/lib/utils'

type EvaluationDimensionRowProps = {
  label: string
  score: number
  reasoning?: string
  className?: string
  barClassName?: string
  reasoningCollapsible?: boolean
  /** `compact` — denser row for report score sections. */
  density?: 'default' | 'compact'
}

export function EvaluationDimensionRow({
  label,
  score,
  reasoning,
  className,
  barClassName,
  reasoningCollapsible = false,
  density = 'default',
}: EvaluationDimensionRowProps) {
  const clamped = Math.max(0, Math.min(1, score))
  const pct = Math.round(clamped * 100)
  const resolvedBarClass = barClassName ?? matchBandBarClass(clamped)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true))
    return () => cancelAnimationFrame(id)
  }, [])

  const reasoningBlock = reasoning ? (
    reasoningCollapsible ? (
      <details>
        <summary className="cursor-pointer list-none text-xs text-muted-foreground hover:text-foreground [&::-webkit-details-marker]:hidden">
          Why this score
        </summary>
        <p className="mt-1 text-xs text-muted-foreground">{reasoning}</p>
      </details>
    ) : (
      <p
        className={cn(
          'text-muted-foreground',
          density === 'compact' ? 'text-xs' : 'text-sm leading-relaxed',
        )}
      >
        {reasoning}
      </p>
    )
  ) : null

  if (density === 'compact') {
    return (
      <div className={cn('space-y-1', className)}>
        <div className="flex items-center gap-2 text-xs">
          <span className="min-w-0 shrink-0 text-muted-foreground">{label}</span>
          <div className="h-1 min-w-0 flex-1 overflow-hidden rounded-full bg-muted">
            <div
              className={cn(
                'h-full rounded-full transition-all motion-reduce:transition-none',
                resolvedBarClass,
              )}
              style={{ width: mounted ? `${pct}%` : 0 }}
              role="presentation"
            />
          </div>
          <span
            className={cn(
              'shrink-0 font-medium tabular-nums',
              barClassName ? 'text-foreground' : matchBandTextClass(clamped),
            )}
          >
            {pct}%
          </span>
        </div>
        {reasoningBlock}
      </div>
    )
  }

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span
          className={cn(
            'font-medium tabular-nums',
            barClassName ? 'text-foreground' : matchBandTextClass(clamped),
          )}
        >
          {pct}%
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-muted">
        <div
          className={cn('h-full rounded-full transition-all motion-reduce:transition-none', resolvedBarClass)}
          style={{ width: mounted ? `${pct}%` : 0 }}
          role="presentation"
        />
      </div>
      {reasoningBlock}
    </div>
  )
}
